import { z } from "zod";
import { TRIP_COLORS } from "@pack-it-up/shared";
import { MAX_TAGS_PER_ROW } from "../usecases/tags.js";
// User-defined tags (T29). Values are normalized by the caller (`normalizeTags`);
// this only guards the count.
const TagsSchema = z.array(z.string()).max(MAX_TAGS_PER_ROW);
export const CreateTripInputSchema = z.object({
    name: z.string().min(1),
    startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    location: z.string().optional(),
    description: z.string().optional(),
    // Issue #8 — copying a trip carries the source's members over, so the copied
    // `specific` assignments name people the new trip actually has. Not something the
    // create form ever sets: it is the source trip's own `members` map, already read and
    // validated, passed straight through. The repository always overwrites the caller's
    // own entry with an owner entry, so a viewer's copy is still owned by them.
    members: z.custom().optional(),
}).refine((d) => !(d.startDate && d.endDate && d.endDate < d.startDate), { message: "endDate must be >= startDate", path: ["endDate"] });
export const TripPatchSchema = z.object({
    tripId: z.string().min(1),
    name: z.string().min(1).optional(),
    startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
    endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
    location: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    color: z.enum(TRIP_COLORS).nullable().optional(),
    visibility: z.enum(["private", "link", "public"]).optional(),
    settings: z.object({
        allowProxyCompletion: z.boolean().optional(),
    }).optional(),
});
export const CreateCategoryInputSchema = z.object({
    name: z.string().min(1),
    parentId: z.string().min(1),
    emoji: z.string().min(1).optional(),
    // Absent = "packing" (legacy default; see Category entity).
    kind: z.enum(["packing", "todo"]).optional(),
});
export const CreateItemInputSchema = z.object({
    categoryId: z.string().min(1),
    name: z.string().min(1),
    quantity: z.number().int().positive().default(1),
    description: z.string().optional(),
    notes: z.string().optional(),
    tags: TagsSchema.optional(),
});
export const ItemPatchSchema = z.object({
    itemId: z.string().min(1),
    name: z.string().min(1).optional(),
    quantity: z.number().int().positive().optional(),
    description: z.string().nullable().optional(),
    notes: z.string().nullable().optional(),
    /** `null` (or an empty array) clears every tag; absent leaves them untouched. */
    tags: TagsSchema.nullable().optional(),
    // The cached provenance flag, re-derived by the caller after a content edit
    // (Doc 02 §7). Writes `provenance.modifiedSinceImport` only — never the rest of
    // the provenance record, which is import/sync-owned.
    modifiedSinceImport: z.boolean().optional(),
});
export const CreateTodoInputSchema = z.object({
    categoryId: z.string().min(1),
    name: z.string().min(1),
    description: z.string().optional(),
    notes: z.string().optional(),
    tags: TagsSchema.optional(),
    dueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
});
//# sourceMappingURL=inputs.js.map